import './MaintenanceOverlay.css';

interface MaintenanceOverlayProps {
  reason: string;
  /**
   * ISO 8601 timestamp marking the end of the maintenance window.
   * Shown to the user as "Regreso estimado · <boarding-pass date>".
   */
  end: string;
}

/**
 * Full-page blocking overlay shown while the platform is in maintenance.
 *
 * Rendered on top of the dashboard (or any route that mounts it). It
 * intercepts pointer events so the user can't reach the buttons beneath
 * while a maintenance window is active. The banner disappears the moment
 * AppContext sees `maintenance === null` (next 30s poll tick).
 *
 * Mount via:
 *   {state.maintenance && (
 *     <MaintenanceOverlay reason={...} end={...} />
 *   )}
 */
export function MaintenanceOverlay({ reason, end }: MaintenanceOverlayProps) {
  const endLocal = formatBoardingPass(end);

  return (
    <div
      className="mo-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="mo-title"
      aria-describedby="mo-reason"
    >
      <article className="mo-card">
        <header className="mo-status">
          <span className="mo-pulse" aria-hidden="true" />
          <span className="mo-eyebrow">Mantenimiento en curso</span>
        </header>

        <h2 id="mo-title" className="mo-title">
          Estamos trabajando en la plataforma.
        </h2>

        <p id="mo-reason" className="mo-reason">
          {reason}
        </p>

        <hr className="mo-divider" />

        <footer className="mo-eta">
          <span className="mo-eta-label">Regreso estimado</span>
          <span className="mo-eta-time">{endLocal}</span>
        </footer>
      </article>
    </div>
  );
}

/**
 * "VIE 09 AGO · 21:30" — uppercase, monospace, dot-separated. Reads as
 * a boarding-pass timestamp rather than a date string. Locale-aware on
 * weekday/month abbreviations (es-AR → "VIE", "AGO"; en-US → "FRI", "AUG").
 */
function formatBoardingPass(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;

  const weekday = d
    .toLocaleString(undefined, { weekday: 'short' })
    .replace('.', '')
    .toUpperCase();
  const day = String(d.getDate()).padStart(2, '0');
  const month = d
    .toLocaleString(undefined, { month: 'short' })
    .replace('.', '')
    .toUpperCase();
  const time = d.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${weekday} ${day} ${month} · ${time}`;
}
